import { useEffect, useId, useRef, useState, type FormEvent } from 'react';

import { submitContact } from '../api/client';
import { ApiError, type Company, type ContactSection } from '../api/types';
import { t } from '../i18n/messages';
import { Reveal } from './Reveal';
import styles from './Contact.module.css';

interface ContactProps {
  section: ContactSection;
  company: Company;
}

type Status =
  | { state: 'idle' }
  | { state: 'sending' }
  | { state: 'sent'; message: string }
  | { state: 'failed'; message: string };

const emptyForm = { name: '', email: '', company: '', message: '', website: '' };

export function Contact({ section, company }: ContactProps) {
  const id = useId();
  const [form, setForm] = useState(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>({ state: 'idle' });

  const formRef = useRef<HTMLFormElement>(null);
  const statusRef = useRef<HTMLParagraphElement>(null);
  const sentRef = useRef<HTMLDivElement>(null);
  /** Bumped on every failed submit so a repeat failure re-runs the focus move. */
  const [failureCount, setFailureCount] = useState(0);

  const sending = status.state === 'sending';
  const sent = status.state === 'sent';

  /**
   * Send the visitor where the problem is. Without this, a failed submit leaves
   * focus on the body: a keyboard or screen-reader user gets no signal at all,
   * and on a wide screen the error summary sits below the fold.
   */
  useEffect(() => {
    if (failureCount === 0) return;

    const firstInvalid = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    // No invalid field means the submit failed as a whole (the API was
    // unreachable, or it rejected everything at once) — the message is the
    // thing to read, so move to that instead.
    (firstInvalid ?? statusRef.current)?.focus();
  }, [failureCount]);

  // The form is gone once sent, so focus has to land somewhere deliberate —
  // otherwise it falls back to the body and the confirmation may sit off-screen.
  useEffect(() => {
    if (sent) sentRef.current?.focus();
  }, [sent]);

  function reset() {
    setStatus({ state: 'idle' });
    setFieldErrors({});
  }

  function update(field: keyof typeof emptyForm, value: string) {
    setForm((previous) => ({ ...previous, [field]: value }));

    // Clear the error the moment the visitor starts fixing it, rather than
    // making them submit again to find out whether they got it right.
    setFieldErrors((previous) => {
      if (!(field in previous)) return previous;
      const next = { ...previous };
      delete next[field];
      return next;
    });

    // Drop the summary line too, otherwise "some of the details need another
    // look" sits there accusingly while they are busy looking at them.
    setStatus((previous) => (previous.state === 'failed' ? { state: 'idle' } : previous));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus({ state: 'sending' });
    setFieldErrors({});

    try {
      const result = await submitContact(form);
      setForm(emptyForm);
      setStatus({
        state: 'sent',
        // The API supplies the confirmation; the fallback only matters if a
        // future response omits it.
        message: result.message ?? t.form.submit,
      });
    } catch (error) {
      // Show the API's own words only when they came from the API. A gateway
      // error page leaves us holding "Request failed (502)", which tells the
      // visitor nothing — and the form is the only way to reach us in writing,
      // so when it is down the useful answer is the phone number.
      const explained = error instanceof ApiError && error.explained;

      setFieldErrors(explained ? (error as ApiError).fields : {});
      setStatus({
        state: 'failed',
        message: explained
          ? (error as ApiError).message
          : t.form.networkError(company.phone),
      });
      setFailureCount((count) => count + 1);
    }
  }

  return (
    <section className="section" id="contact">
      <div className="shell">
        <div className={styles.top}>
          <Reveal>
            <p className="eyebrow">{section.eyebrow}</p>
            <h2 className={styles.title}>{section.title}</h2>
            {section.body ? <p className={`lead ${styles.body}`}>{section.body}</p> : null}
          </Reveal>

          <Reveal className={styles.channels}>
            <a className={styles.channel} href={`tel:${company.phoneHref}`}>
              <b>{t.channels.phone}</b>
              <span className={styles.channelValue}>{company.phone}</span>
              <small>{company.officeHours}</small>
            </a>

            <div className={styles.channel}>
              <b>{t.channels.where}</b>
              <span className={styles.channelValue}>{company.location}</span>
              <small>{t.channels.whereHint}</small>
            </div>
          </Reveal>
        </div>

        <Reveal>
          {sent ? (
            <div className={styles.sent} ref={sentRef} role="status" tabIndex={-1}>
              <span className={styles.sentIcon} aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none">
                  <path
                    d="M4 10.5l4 4 8-9"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className={styles.sentTitle}>{t.form.sentTitle}</span>
              <p className={styles.sentMessage}>{status.message}</p>
              <button className="btn btnGhost" type="button" onClick={reset}>
                {t.form.sendAnother}
              </button>
            </div>
          ) : (
            <form className={styles.form} ref={formRef} onSubmit={handleSubmit} noValidate>
            <p className={styles.formIntro}>{section.formIntro}</p>

            <div className={styles.row}>
              <div className={styles.field} data-invalid={Boolean(fieldErrors.name)}>
                <label htmlFor={`${id}-name`}>{t.form.name}</label>
                <input
                  id={`${id}-name`}
                  name="name"
                  autoComplete="name"
                  value={form.name}
                  onChange={(event) => update('name', event.target.value)}
                  aria-invalid={Boolean(fieldErrors.name)}
                  aria-describedby={fieldErrors.name ? `${id}-name-error` : undefined}
                  disabled={sending}
                  required
                />
                {fieldErrors.name ? (
                  <span className={styles.fieldError} id={`${id}-name-error`}>
                    {fieldErrors.name}
                  </span>
                ) : null}
              </div>

              <div className={styles.field} data-invalid={Boolean(fieldErrors.email)}>
                <label htmlFor={`${id}-email`}>{t.form.email}</label>
                <input
                  id={`${id}-email`}
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(event) => update('email', event.target.value)}
                  aria-invalid={Boolean(fieldErrors.email)}
                  aria-describedby={fieldErrors.email ? `${id}-email-error` : undefined}
                  disabled={sending}
                  required
                />
                {fieldErrors.email ? (
                  <span className={styles.fieldError} id={`${id}-email-error`}>
                    {fieldErrors.email}
                  </span>
                ) : null}
              </div>
            </div>

            <div className={styles.field} data-invalid={Boolean(fieldErrors.company)}>
              <label htmlFor={`${id}-company`}>{t.form.company}</label>
              <input
                id={`${id}-company`}
                name="company"
                autoComplete="organization"
                value={form.company}
                onChange={(event) => update('company', event.target.value)}
                disabled={sending}
              />
            </div>

            <div className={styles.field} data-invalid={Boolean(fieldErrors.message)}>
              <label htmlFor={`${id}-message`}>{t.form.message}</label>
              <textarea
                id={`${id}-message`}
                name="message"
                value={form.message}
                onChange={(event) => update('message', event.target.value)}
                placeholder={t.form.messagePlaceholder}
                aria-invalid={Boolean(fieldErrors.message)}
                aria-describedby={fieldErrors.message ? `${id}-message-error` : undefined}
                disabled={sending}
                required
              />
              {fieldErrors.message ? (
                <span className={styles.fieldError} id={`${id}-message-error`}>
                  {fieldErrors.message}
                </span>
              ) : null}
            </div>

            <div className={styles.honeypot} aria-hidden="true">
              <label htmlFor={`${id}-website`}>{t.form.honeypot}</label>
              <input
                id={`${id}-website`}
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(event) => update('website', event.target.value)}
              />
            </div>

              <div className={styles.submitRow}>
                <button className="btn btnPrimary" type="submit" disabled={sending}>
                  {sending ? t.form.submitting : t.form.submit}
                </button>

                {status.state === 'failed' ? (
                  <p
                    className={styles.status}
                    data-tone="error"
                    ref={statusRef}
                    // A failure interrupts what the visitor was doing, so it is
                    // announced immediately. Success has its own panel above.
                    role="alert"
                    // Focusable only as a target for the move above — never a tab stop.
                    tabIndex={-1}
                  >
                    {status.message}
                  </p>
                ) : null}
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
